/**
 * This module contains logic for tracking various experiments. Experiments
 * can be enabled/disabled using query parameters:
 *   enable:  http://foo.com/?enableExperiments=experimentOne,experimentTwo
 *   disable: http://foo.com/?disableExperiments=experimentOne,experimentTwo
 * Experiment state is persisted across page loads using local storage.  Note
 * that it's only written when isEnabled is called for the key in question.
 */
import Cookie from 'js-cookie';

import DCDO from '@cdo/apps/dcdo';

import {trySetLocalStorage} from '../utils';

const queryString = require('query-string');

const experiments = module.exports;
// Needed to support TypeScript usage.
export default experiments;
const STORAGE_KEY = 'experimentsList';
const EXPERIMENT_LIFESPAN_HOURS = 12;

// Specific experiment names
experiments.REDUX_LOGGING = 'reduxLogging';
experiments.SCHOOL_AUTOCOMPLETE_DROPDOWN_NEW_SEARCH =
  'schoolAutocompleteDropdownNewSearch';
experiments.SHOW_UNPUBLISHED_DATASET_TABLES = 'showUnpublishedDatasetTables';
experiments.I18N_TRACKING = 'frontend-i18n-tracking';
experiments.BYPASS_DIALOG_POPUP = 'bypass-dialog-popup';
experiments.SPECIAL_TOPIC = 'special-topic';
// Experiment for showing a backgrounds tab and enabling student upload
// for Sprite Lab animations
experiments.BACKGROUNDS_AND_UPLOAD = 'backgroundsTab';
// Experiment for showing the gender field
experiments.GENDER_FEATURE_ENABLED = 'gender';
// Experiment for enabling the AI-TA differentiation chat
experiments.AI_DIFFERENTIATION = 'ai-differentiation';
// Experiment for enabling the AI-TA differentiation artifacts
experiments.AI_ARTIFACT = 'ai-artifact';
// Adds a "Get help with this block" option to block context menus if docs exist (e.g. Sprite Lab)
experiments.BLOCKLY_DOCS = 'blockly_docs';
// Allows the playspace to be dragged to take up a larger portion of the screen
experiments.BIG_PLAYSPACE = 'bigPlayspace';
// Use glow effect for Blockly block highlighting
experiments.BLOCKLY_GLOW_HIGHLIGHT = 'blockly-glow-highlight';
// Turn on Blockly Keyboard Navigation
experiments.BLOCKLY_KEYBOARD_NAVIGATION = 'blockly-keyboard-navigation';
// Use nested course URLs like /courses/csd-2024/units/1/...
experiments.MODULARITY = 'modularity';
// LocalizeJS
experiments.LOCALIZEJS = 'localizejs';
// Enable ActionCable load testing
experiments.ACTIONCABLE_LOAD_TESTING = 'actioncable-load-testing';
// Use AI Tutor system prompts from Langfuse
experiments.USE_LANGFUSE_PROMPT = 'use-langfuse-prompt';
// Enable AI lesson podcasts
experiments.AI_LESSON_PODCASTS = 'ai-lesson-podcasts';
// Use channel-id based preview urls on localhost for Web Lab 2
experiments.WEBLAB2_FULL_URLS = 'weblab2-full-urls';
// Show unified diff view in Code Editor.
experiments.ACCEPT_REJECT_UNIFIED_DIFF = 'accept-reject-unified-diff';
// Show split diff view in Code Editor.
experiments.ACCEPT_REJECT_SPLIT_DIFF = 'accept-reject-split-diff';
// Show the lesson/<lesson_id>/tutor page as a home for a AI Tutor+
// and shows the review path for AI Tutor+
experiments.LESSON_TUTOR = 'lesson-tutor';
// Show the challenge path for AI Tutor+
experiments.LESSON_TUTOR_CHALLENGE = 'lesson-tutor-challenge';
// Enable Onboarding experiments
experiments.ONBOARDING = 'onboarding';
// Enable AI Diff Chat Drawer
experiments.AI_DIFF_DRAWER = 'ai-diff-drawer';
// Route all Gemini model traffic through the AI gateway instead of the Rails backend
experiments.USE_AI_GATEWAY = 'useAiGateway';
// Enable speech-to-text input in AI chat lab and AI tutor for all models
experiments.ENABLE_SPEECH_TO_TEXT = 'enable-speech-to-text';
// Run the pyodide worker in a hidden iframe on a separate sandboxed-preview
// subdomain (see apps/src/pythonlab/README.md), isolated from studio.code.org's
// cookies/session, instead of directly on studio.code.org.
experiments.PYTHONLAB_SEPARATE_DOMAIN = 'pythonlab-separate-domain';
// Per-session opt-in to the codeaiprojects.org sandboxed-preview domain ahead
// of the global 'sandboxed-preview-domain' DCDO flag, for production bug
// bashes. See getPreviewDomain() in apps/src/util/sandboxedPreviewDomain.ts.
experiments.NEW_PREVIEW_DOMAIN = 'new-preview-domain';
// Student scrapbook entrypoint + "My scrapbook" dropdown link.
// Enable with ?student-scrapbook=true or ?enableExperiments=student-scrapbook.
experiments.STUDENT_SCRAPBOOK = 'student-scrapbook';

/**
 * Get our query string. Provided as a method so that tests can mock this.
 */
experiments.getQueryString_ = function () {
  return window.location.search;
};

experiments.getLocalStorageExperiments_ = function () {
  try {
    const jsonList = localStorage.getItem(STORAGE_KEY);
    const storedExperiments = jsonList ? JSON.parse(jsonList) : [];
    const now = Date.now();
    // Storage may contain duplicate keys; keep the first live entry for each.
    const seenKeys = new Set();
    const enabledExperiments = storedExperiments.filter(experiment => {
      const enabled =
        experiment.key &&
        !seenKeys.has(experiment.key) &&
        (experiment.expiration === undefined || experiment.expiration > now);
      if (enabled) {
        seenKeys.add(experiment.key);
      }
      return enabled;
    });
    if (enabledExperiments.length < storedExperiments.length) {
      trySetLocalStorage(STORAGE_KEY, JSON.stringify(enabledExperiments));
    }
    return enabledExperiments;
  } catch (e) {
    return [];
  }
};

experiments.getStoredExperiments_ = function () {
  const experimentsCookie = Cookie.get('_experiments' + window.cookieEnvSuffix);
  const userExperiments = experimentsCookie
    ? JSON.parse(decodeURIComponent(experimentsCookie)).map(name => ({
        key: name,
      }))
    : [];

  return userExperiments.concat(this.getLocalStorageExperiments_());
};

experiments.getEnabledExperiments = function () {
  return this.getStoredExperiments_().map(experiment => experiment.key);
};

/**
 * Returns the experiments enabled in this browser via local storage (the kind
 * setEnabled manages), along with the metadata we store for them. Does not
 * include user experiments mirrored into the _experiments cookie at sign-in.
 * @returns {Array<{key: string, expiration?: number}>} expiration is ms since
 *   epoch, present only for temporarily-enabled experiments.
 */
experiments.getLocalStorageExperimentDetails = function () {
  return this.getLocalStorageExperiments_();
};

experiments.setEnabled = function (key, shouldEnable, expiration = undefined) {
  const allEnabled = this.getLocalStorageExperiments_();
  const experimentIndex = allEnabled.findIndex(
    experiment => experiment.key === key
  );
  if (shouldEnable) {
    if (experimentIndex < 0) {
      allEnabled.push({key, expiration});
    } else {
      allEnabled[experimentIndex].expiration = expiration;
    }
  } else if (experimentIndex >= 0) {
    allEnabled.splice(experimentIndex, 1);
  } else {
    return;
  }
  trySetLocalStorage(STORAGE_KEY, JSON.stringify(allEnabled));
};

/**
 * Checks for the experiment while allowing for a simpler query string
 * parameter to enable the experiment. For instance, if `key` is "foo",
 * the experiment is allowed by any other means but also if `?foo=1` is
 * specified in the current URL.
 * @param {string} key - Name of experiment in question
 * @returns {bool}
 */
experiments.isEnabledAllowingQueryString = function (key) {
  const query = queryString.parse(this.getQueryString_());

  // Look for ?my_experiment=1 style experiment keys
  if (query[key]) {
    // We enable when any query string matches, but do not
    // set it in the session storage.
    return true;
  }

  return experiments.isEnabled(key);
};

/**
 * Checks whether provided experiment is enabled or not.
 * @param {string} key - Name of experiment in question
 * @returns {bool}
 */
experiments.isEnabled = function (key) {
  const storedExperiments = this.getStoredExperiments_();
  let enabled =
    storedExperiments.some(experiment => experiment.key === key) ||
    !!(
      window.appOptions &&
      window.appOptions.experiments &&
      window.appOptions.experiments.includes(key)
    );
  // Check DCDO to see if this experiment is enabled.
  // User experiment flags and cookie experiment flags take higher priority over DCDO experiments.
  enabled = enabled || !!DCDO.get(key, false);

  const query = queryString.parse(this.getQueryString_());
  const enableQuery = query['enableExperiments'];
  const disableQuery = query['disableExperiments'];
  const tempEnableQuery = query['tempEnableExperiments'];

  if (enableQuery) {
    const experimentsToEnable = enableQuery.split(',');
    if (experimentsToEnable.indexOf(key) >= 0) {
      enabled = true;
      this.setEnabled(key, true);
    }
  }

  if (disableQuery) {
    const experimentsToDisable = disableQuery.split(',');
    if (experimentsToDisable.indexOf(key) >= 0) {
      enabled = false;
      this.setEnabled(key, false);
    }
  }

  if (tempEnableQuery) {
    const expirationDate = new Date();
    expirationDate.setHours(
      expirationDate.getHours() + EXPERIMENT_LIFESPAN_HOURS
    );
    const expiration = expirationDate.getTime();

    const experimentsToEnable = tempEnableQuery.split(',');
    if (experimentsToEnable.indexOf(key) >= 0) {
      enabled = true;
      this.setEnabled(key, true, expiration);
    }
  }

  return enabled;
};
